import React, { createContext, useContext, useState, useEffect } from 'react';
import { setApiKey as setGlobalApiKey, setWorkingEndpoint as setGlobalWorkingEndpoint } from '../components/pioagent/services/apiClient';
import { validateApiKeyOnEndpoint } from '../components/pioagent/services/authService';
import { useUser as useMainUser } from './UserContext';

const PioAgentUserContext = createContext(null);

export const PioAgentUserProvider = ({ children }) => {
  const { isAuthenticated, user, isAdmin: mainIsAdmin, isSuperAdmin: mainIsSuperAdmin } = useMainUser();
  
  const [apiKey, setApiKeyState] = useState('');
  const [workingEndpoint, setWorkingEndpointState] = useState('');
  const [apiConfigured, setApiConfigured] = useState(false);
  const [apiRole, setApiRole] = useState('user');

  const configureApi = async (newApiKey, newEndpoint) => {
    try {
      const result = await validateApiKeyOnEndpoint(newApiKey, newEndpoint);

      if (result.success) {
        const role = result.data?.role || 'user';

        setApiKeyState(newApiKey);
        setWorkingEndpointState(newEndpoint);
        setApiConfigured(true);
        setApiRole(role);

        setGlobalApiKey(newApiKey);
        setGlobalWorkingEndpoint(newEndpoint);

        localStorage.setItem('apiKey', newApiKey);
        localStorage.setItem('workingEndpoint', newEndpoint);
        localStorage.setItem('apiRole', role);

        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Connection error. Please try again.' };
    }
  };

  const logout = () => {
    window.location.href = '/';
  };

  useEffect(() => {
    const loadApiConfig = async () => {
      const currentPath = window.location.pathname;
      const isPioAgentPage = currentPath.startsWith('/pioagent');
      
      if (isPioAgentPage) {
        const agentContextStr = sessionStorage.getItem('pioagent_agent_context');
        if (agentContextStr) {
          try {
            const agentContext = JSON.parse(agentContextStr);
            if (agentContext.apiKey && agentContext.endpoint) {
              const result = await configureApi(agentContext.apiKey, agentContext.endpoint);
              if (result.success) {
                sessionStorage.setItem('pioagent_current_agent', agentContextStr);
                return;
              }
            }
          } catch (error) {
          }
        }
      }

      if (!isPioAgentPage) {
        const storedApiKey = localStorage.getItem('apiKey');
        const storedEndpoint = localStorage.getItem('workingEndpoint');
        const storedRole = localStorage.getItem('apiRole');

        if (storedApiKey && storedEndpoint) {
          setApiKeyState(storedApiKey);
          setWorkingEndpointState(storedEndpoint);
          setApiRole(storedRole || 'user');
          setGlobalApiKey(storedApiKey);
          setGlobalWorkingEndpoint(storedEndpoint);
          setApiConfigured(true);
        }
      }
    };

    loadApiConfig();
    
    const handleLocationChange = () => {
      loadApiConfig();
    };
    
    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const clearApiConfig = () => {
    setApiKeyState('');
    setWorkingEndpointState('');
    setApiConfigured(false);
    setApiRole('user');
    setGlobalApiKey('');
    setGlobalWorkingEndpoint('');
    localStorage.removeItem('apiKey');
    localStorage.removeItem('workingEndpoint');
    localStorage.removeItem('apiRole');
  };

  const isAdmin = apiRole === 'admin' || mainIsAdmin;
  const isSuperAdmin = mainIsSuperAdmin;

  const hasAccess = (feature) => {
    return isAuthenticated;
  };

  const value = {
    isAuthenticated,
    loading: false,
    user,
    roles: [],
    token: null,
    login: () => {},
    logout,
    isSuperAdmin,
    isAdmin,
    hasAccess,
    apiKey,
    workingEndpoint,
    apiConfigured,
    apiRole,
    configureApi,
    clearApiConfig
  };

  return (
    <PioAgentUserContext.Provider value={value}>
      {children}
    </PioAgentUserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(PioAgentUserContext);
  if (!context) {
    throw new Error('useUser must be used within a PioAgentUserProvider');
  }
  return context;
};

